
import { CalorieDetail, Ingredients, Menu, OutputDetail, Point, SimpleOutput, Triangle } from '@/entity/entity'
import { Route } from '@/entity/route'
import { api } from '@/scripts/api'
import { convertMenuDataToRequestData } from '@/scripts/data'
import { StorageAccess } from '@/scripts/storage-access'
import { convertNumberToMoneyFormat } from '@/scripts/util'
import Vue from 'vue'

const config = {
  top: 76,
  bottom: 40,
  right: 140,
  left: 58,
  width: 380,
  height: 328,
  lengthProtain: 220
}
const alpha = Math.atan2(config.height - config.lengthProtain, config.width / 2)
const lengthFatCarbon = Math.sqrt(Math.pow(config.height - config.lengthProtain, 2) + Math.pow(config.width / 2, 2))

export default Vue.extend({
  name: 'Calculation',
  created: function () {
    if (this.menus.length === 0) {
      this.$router.push(Route.MenuList.path)
      return
    }
    api.requestCalculation(convertMenuDataToRequestData(this.menus)).then((res) => {
      if (res === 'error') {
        alert('Something went wrong, please try again')
        this.$router.push(Route.MenuList.path)
        return
      }
      // Assign calories
      this.calories = res.detail.detail_calorie_base.map(x => ({
        name: this.ingredients[x.food_code].name ?? '',
        type: x.food_type,
        amountOfConsumption: x.amount_of_consumption,
        nutrientEnergy: x.nutrient_energy,
        sSRate: x.self_sufficiency_rate,
        producingDistrict: x.producing_district
      }))
      this.totalCalorieA = res.detail.total_calorie_base_A
      this.totalCalorieB = res.detail.total_calorie_base_B
      // Assign output
      this.output = res.detail.detail_output_base.map(x => ({
        name: this.ingredients[x.food_code].name ?? '',
        type: x.food_type,
        amountOfConsumption: x.amount_of_consumption,
        weightBasedSSRate: x.weight_based_self_sufficiency_rate,
        producingDistrict: x.producing_district
      }))
      this.totalOutputA = res.detail.total_output_base_A
      this.totalOutputB = res.detail.total_output_base_B
      // Assign Simple Output
      this.simpleOutput = {
        calorieBaseSufficiencyRate: res.simple_output.calorie_base_sufficiency_rate,
        outputBaseSufficiencyRate: res.simple_output.output_base_sufficiency_rate,
        energy: convertNumberToMoneyFormat(Number(res.simple_output.energy)),
        energyRaw: res.simple_output.energy_raw,
        protein: convertNumberToMoneyFormat(Number(res.simple_output.protein)),
        proteinRaw: res.simple_output.protein_raw,
        fat: convertNumberToMoneyFormat(Number(res.simple_output.fat)),
        fatRaw: res.simple_output.fat_raw,
        carbohydrate: res.simple_output.carbohydrate,
        carbohydrateRaw: res.simple_output.carbohydrate_raw,
        pfcProtein: Number(res.simple_output.pfc_protein),
        pfcFat: Number(res.simple_output.pfc_fat),
        pfcCarbohydrate: Number(res.simple_output.pfc_carbohydrate)
      }
    })
  },
  props: {
    ingredients: {
      type: Object as () => Ingredients,
      required: true
    }
  },
  data: function () : {
    listLink: string,
    showCalorie: boolean,
    showEnergy: boolean,
    showBalance: boolean,
    menus: Menu[],
    calories: CalorieDetail[],
    totalCalorieA: number,
    totalCalorieB: number,
    output: OutputDetail[],
    totalOutputA: number,
    totalOutputB: number,
    simpleOutput: SimpleOutput,
    width: number,
    height: number,
    originPoint: Point,
    lengthProtain: number
    } {
    return {
      listLink: Route.MenuList.path,
      showCalorie: false,
      showEnergy: false,
      showBalance: false,
      menus: StorageAccess.getMenus(),
      calories: [],
      totalCalorieA: 0,
      totalCalorieB: 1,
      output: [],
      totalOutputA: 0,
      totalOutputB: 1,
      simpleOutput: {
        calorieBaseSufficiencyRate: '',
        outputBaseSufficiencyRate: '',
        energy: '',
        protein: '',
        fat: '',
        carbohydrate: '',
        energyRaw: '',
        proteinRaw: '',
        fatRaw: '',
        carbohydrateRaw: '',
        pfcProtein: 0,
        pfcFat: 0,
        pfcCarbohydrate: 0
      },
      // Chart SVG
      width: config.left + config.width + config.right,
      height: config.top + config.height + config.bottom,
      originPoint: {
        x: config.left + config.width / 2,
        y: config.top + config.lengthProtain
      },
      lengthProtain: config.lengthProtain
    }
  },
  computed: {
    totalCalorieAText: function () : string {
      return convertNumberToMoneyFormat(Math.round(this.totalCalorieA))
    },
    totalCalorieBText: function () : string {
      return convertNumberToMoneyFormat(Math.round(this.totalCalorieB))
    },
    totalOutputAText: function () : string {
      return convertNumberToMoneyFormat(Math.round(this.totalOutputA))
    },
    totalOutputBText: function () : string {
      return convertNumberToMoneyFormat(Math.round(this.totalOutputB))
    },
    maxProtainPoint: function () : Point {
      return {
        x: this.originPoint.x,
        y: this.originPoint.y - config.lengthProtain
      }
    },
    maxFatPoint: function () : Point {
      return {
        x: this.originPoint.x - (config.width / 2),
        y: this.originPoint.y + (config.height - config.lengthProtain)
      }
    },
    maxCarbonPoint: function () : Point {
      return {
        x: this.originPoint.x + (config.width / 2),
        y: this.maxFatPoint.y
      }
    },
    // Chart SVG
    chartTriangle: function () : Triangle {
      return this.getTriangle(this.simpleOutput.pfcProtein / 100, this.simpleOutput.pfcFat / 100, this.simpleOutput.pfcCarbohydrate / 100)
    },
    twentyPercentTriangle: function () : Triangle {
      return this.getTriangle(0.2, 0.2, 0.2)
    },
    fourtyPercentTriangle: function () : Triangle {
      return this.getTriangle(0.4, 0.4, 0.4)
    }
  },
  methods: {
    toggleCalorie: function () {
      this.showCalorie = !this.showCalorie
    },
    toggleEnergy: function () {
      this.showEnergy = !this.showEnergy
    },
    toggleBalance: function () {
      this.showBalance = !this.showBalance
    },
    getTriangle: function (protain: number, fat: number, carbon: number) : Triangle {
      return {
        protain: {
          x: this.originPoint.x,
          y: this.originPoint.y - (config.lengthProtain * protain)
        },
        fat: {
          x: this.originPoint.x - (Math.cos(alpha) * lengthFatCarbon * fat),
          y: this.originPoint.y + (Math.sin(alpha) * lengthFatCarbon * fat)
        },
        carbon: {
          x: this.originPoint.x + (Math.cos(alpha) * lengthFatCarbon * carbon),
          y: this.originPoint.y + (Math.sin(alpha) * lengthFatCarbon * carbon)
        }
      }
    }
  }
})
